import "./index.css";
import { Badge, Drawer, IconButton, Popover, Tooltip } from "@/ui-kit";
import { BellIcon } from "lucide-react";
import { Notifications } from "../../index";
import { useNotificationsStore } from "@/store/notifications/slice";
import { useCallback, useEffect, useRef } from "react";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import { useLocation } from "react-router";
import type { IStreamNotificationsResponse } from "@/api/notifications/types";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useTranslation } from "react-i18next";
import { HeaderMenu } from "@/components/Notifications/components/HeaderMenu";

// Paths where notification stream should not be initialized
const blacklistUrl: string[] = [
  "select-tenant",
  "auth",
  "login",
  "logout",
  "register",
];

export const NotificationButton = () => {
  const { t } = useTranslation();
  const count = useNotificationsStore((s) => s.count);
  const open = useNotificationsStore((s) => s.open);
  const streamRef = useRef<IStreamNotificationsResponse | null>(null);
  const { pathname } = useLocation();
  const isBlacklisted = blacklistUrl.some((url) => {
    return pathname.includes(url);
  });
  const { isMobile } = useBreakpoints();

  const onOpenChange = useCallback(() => {
    useNotificationsStore
      .getState()
      .setOpen(!useNotificationsStore.getState().open);
  }, []);

  useEffect(() => {
    const repository = new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });

    const connectStream = () => {
      streamRef.current = repository.stream({
        onUnreadCount: (unreadCount) => {
          useNotificationsStore.getState().setCount(unreadCount);
        },
        onClose: (error) => {
          if (!error) {
            connectStream();
          }
        },
      });
    };

    connectStream();

    return () => {
      streamRef.current?.close();
    };
  }, []);

  if (isBlacklisted) {
    return null;
  }

  if (isMobile) {
    return (
      <>
        <IconButton
          color="layer1"
          size="md"
          content={
            <Badge
              className="notifications-badge"
              size="md"
              hide={!count}
              anchor={<BellIcon />}
              content={count}
            />
          }
          onClick={onOpenChange}
          aria-label={t("label.notifications")}
        />
        {open && (
          <Drawer
            open={true}
            onOpenChange={onOpenChange}
            className="notifications-drawer"
            title={t("label.notifications")}
            accessory={<HeaderMenu />}
          >
            <Notifications />
          </Drawer>
        )}
      </>
    );
  }

  return (
    <Tooltip content={t("label.notifications")} placement="bottom-end">
      <Popover
        open={open}
        onOpenChange={onOpenChange}
        content={<Notifications />}
        contentClassName="notifications-dropdown-content"
        placement="bottom-end"
        maxWidth={420}
      >
        <IconButton
          color="layer1"
          size="md"
          content={
            <Badge
              className="notifications-badge"
              size="md"
              hide={!count}
              anchor={<BellIcon />}
              content={count}
            />
          }
          aria-label={t("label.notifications")}
        />
      </Popover>
    </Tooltip>
  );
};
